import { useId } from 'react'

export function computeRayOriginXPct(activeIndex: number, totalTiers: number): number {
  const clamped = Math.max(0, Math.min(totalTiers - 1, activeIndex))
  return ((clamped + 0.5) / totalTiers) * 100
}

export interface RayBurstBackdropProps {
  /** Index of the active tier (0-based). Defaults to the centered tier. */
  activeIndex?: number
  /** Total number of tiers in the row. Defaults to 5. */
  totalTiers?: number
}

const RAY_BOX = 1000
const RAY_LENGTH = 420
const HALO_RADIUS = 460

export function RayBurstBackdrop({ activeIndex, totalTiers = 5 }: RayBurstBackdropProps) {
  const effectiveIndex = activeIndex ?? (totalTiers - 1) / 2
  const xPct = computeRayOriginXPct(effectiveIndex, totalTiers)
  const uid = useId()
  const rayId = `pt-me-ray-${uid}`
  const haloId = `pt-me-halo-${uid}`

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden text-primary"
      aria-hidden
    >
      {/* Container mirrors medallion row's horizontal extent (px-3/sm:px-6 + max-w-5xl mx-auto).
          xPct% of this container = active medallion center. Responsive --ray-y matches the
          badge's vertical center at each breakpoint. */}
      <div className="absolute inset-0 flex px-3 sm:px-6">
        <div className="relative w-full max-w-5xl mx-auto">
          <svg
            className="absolute top-[32%] sm:top-[40%] md:top-[38%]"
            style={{
              left: `${xPct}%`,
              width: RAY_BOX,
              height: RAY_BOX,
              transform: 'translate(-50%, -50%)',
              overflow: 'visible',
            }}
            viewBox={`${-RAY_BOX / 2} ${-RAY_BOX / 2} ${RAY_BOX} ${RAY_BOX}`}
          >
            <defs>
              <linearGradient id={rayId} x1="0.5" y1="0" x2="0.5" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.14" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
              <radialGradient id={haloId} cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.11" />
                <stop offset="35%" stopColor="currentColor" stopOpacity="0.06" />
                <stop offset="70%" stopColor="currentColor" stopOpacity="0.015" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Soft radial halo — alpha fades smoothly to 0 at its edge.
                No element boundary inside visible area → no shadow line. */}
            <circle cx="0" cy="0" r={HALO_RADIUS} fill={`url(#${haloId})`} />
            {/* 12 rays converging at origin (pixel-locked behind the active badge) */}
            {Array.from({ length: 12 }).map((_, i) => (
              <polygon
                key={i}
                points={`0,0 40,${RAY_LENGTH} -40,${RAY_LENGTH}`}
                fill={`url(#${rayId})`}
                transform={`rotate(${(i / 12) * 360})`}
              />
            ))}
          </svg>
        </div>
      </div>
    </div>
  )
}
